import type { SaleClose } from "@/domain/activity/types";
import type { SlotHours } from "./rules";

/** Days are read as local calendar days — only the day, month and year of the
 *  bounds count. */
export interface SeriesPattern {
  /** `Date.getDay()` numbering: 0 = neděle. */
  weekdays: number[];
  startTime: string;
  endTime: string;
  from: Date;
  to: Date;
}

/** Independent from the moment it is created — nothing links it to the others
 *  (ADR: a series is not an entity). */
export interface PlannedOffer {
  /** The calendar day it falls on, `"yyyy-MM-dd"`. The pattern puts one termín
   *  on a day, and correcting a row moves its hours, never its day — so this
   *  identifies a row across every correction. */
  id: string;
  startTime: Date;
  endTime: Date;
  /** Derived from the šablona's lead time. Null leaves the opening to the
   *  API's own default, exactly as an empty field does in the single form. */
  saleOpensAt: Date | null;
  /** Derived from the šablona's sale-close offset. Null leaves the closing to
   *  the API's own default. */
  saleClosesAt: Date | null;
}

export interface SaleWindowDefaults {
  leadTimeHours: number | null;
  close: SaleClose | null;
}

export const noSaleWindowDefaults: SaleWindowDefaults = { leadTimeHours: null, close: null };

export const seriesMessage = {
  noWeekday: "Vyberte aspoň jeden den v týdnu.",
  noHours: "Zadejte čas začátku i konce termínu.",
  endEqualsStart: "Konec termínu nesmí být stejný jako jeho začátek.",
  emptyRange: "Konec období nesmí být dřív než jeho začátek.",
  generatesNothing: "V zadaném období nevychází ani jeden z vybraných dnů.",
  nothingLeft: "Nezbyl žádný termín k vypsání.",
  publishedUnknown:
    "Zatím nevíme, které termíny už máte vypsané. Počkejte, než je načteme, a zkuste to znovu.",
  templateWithoutPrice:
    "Šablona slev nemá výchozí zlevněnou cenu — doplňte ji v šabloně a vraťte se sem.",
  templateWithoutCapacity:
    "Šablona slev nemá výchozí počet míst — doplňte ho v šabloně a vraťte se sem.",
  rowsEndWhenTheyStart:
    "Některé termíny končí ve chvíli, kdy začínají. Upravte jejich čas v přehledu.",
  startsInPast: "Některé termíny už začaly. Odeberte je z přehledu.",
  saleClosesBeforeItOpens:
    "Konec prodeje vychází před jeho začátkem — u některých termínů upravte čas nebo je odeberte.",
  saleClosesAfterItEnds:
    "Konec prodeje vychází po konci termínu — u některých termínů upravte čas nebo je odeberte.",
  saleAlreadyClosed:
    "Prodej některých termínů by skončil dřív, než je zveřejníte — upravte čas nebo je odeberte.",
  collidesWithPublished: "Některé termíny se překrývají s už vypsanými. Odeberte je z přehledu.",
  collidesWithinSeries: "Některé termíny se překrývají navzájem. Odeberte je z přehledu.",
} as const;

const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

function timeParts(time: string): { hours: number; minutes: number } | null {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
}

/** Real minutes from the anchor, so the closing follows a daylight-saving shift
 *  the way the sale opening does. Both ends are instants, which is how an
 *  overnight termín counts from its end on the next day. */
export function saleClosesAt(startsAt: Date, endsAt: Date, close: SaleClose | null): Date | null {
  if (close === null) return null;
  const anchor = close.anchor === "start" ? startsAt : endsAt;
  return new Date(anchor.getTime() + close.offsetMinutes * MINUTE_MS);
}

/** The end a template length implies, as wall-clock time like the pattern's own
 *  field. A full day lands back on the start, which is no end at all — empty. */
export function endTimeAfter(startTime: string, durationMinutes: number | null): string {
  const start = timeParts(startTime);
  if (!start || durationMinutes === null) return "";
  const endMinutes = (minutesOfDay(start) + durationMinutes) % MINUTES_IN_DAY;
  if (endMinutes === minutesOfDay(start)) return "";
  const hours = String(Math.floor(endMinutes / MINUTES_IN_HOUR)).padStart(2, "0");
  const minutes = String(endMinutes % MINUTES_IN_HOUR).padStart(2, "0");
  return `${hours}:${minutes}`;
}

export function startOfLocalDay(instant: Date): Date {
  return new Date(instant.getFullYear(), instant.getMonth(), instant.getDate());
}

function atWallClock(day: Date, time: { hours: number; minutes: number }): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), time.hours, time.minutes);
}

export function dayKey(day: Date): string {
  const month = String(day.getMonth() + 1).padStart(2, "0");
  const dayOfMonth = String(day.getDate()).padStart(2, "0");
  return `${day.getFullYear()}-${month}-${dayOfMonth}`;
}

/** An end earlier than the start means the termín runs past midnight onto the
 *  next day. An equal end is not overnight — it is refused, not shown as one. */
export function endsNextDay(startTime: string, endTime: string): boolean {
  const start = timeParts(startTime);
  const end = timeParts(endTime);
  if (!start || !end) return false;
  return minutesOfDay(end) < minutesOfDay(start);
}

export function endsWhenItStarts(startTime: string, endTime: string): boolean {
  const start = timeParts(startTime);
  const end = timeParts(endTime);
  if (!start || !end) return false;
  return minutesOfDay(end) === minutesOfDay(start);
}

function hasHours(pattern: SeriesPattern): boolean {
  return timeParts(pattern.startTime) !== null && timeParts(pattern.endTime) !== null;
}

/** The rows the pattern stands for, one per matching day. Times are wall clock:
 *  an evening stays an evening across a daylight-saving change, while the sale
 *  opening is real hours before it, so it follows the clock shift. */
export function expandSeriesPattern(
  pattern: SeriesPattern,
  saleDefaults: SaleWindowDefaults,
): PlannedOffer[] {
  const start = timeParts(pattern.startTime);
  const end = timeParts(pattern.endTime);
  if (!start || !end) return [];
  if (pattern.weekdays.length === 0) return [];

  const lastDay = startOfLocalDay(pattern.to);
  const rows: PlannedOffer[] = [];
  for (
    const day = startOfLocalDay(pattern.from);
    day.getTime() <= lastDay.getTime();
    day.setDate(day.getDate() + 1)
  ) {
    if (!pattern.weekdays.includes(day.getDay())) continue;
    const row = planOffer(
      dayKey(day),
      day,
      pattern.startTime,
      pattern.endTime,
      saleDefaults,
    );
    if (row) rows.push(row);
  }
  return rows;
}

/** Also how a corrected row is rebuilt, so an adjusted hour drags its own sale
 *  opening and closing with it. Null = the hours are not a pair of times yet. */
export function planOffer(
  id: string,
  day: Date,
  startTime: string,
  endTime: string,
  { leadTimeHours, close }: SaleWindowDefaults,
): PlannedOffer | null {
  const start = timeParts(startTime);
  const end = timeParts(endTime);
  if (!start || !end) return null;

  const startsAt = atWallClock(day, start);
  const endDay = new Date(day);
  if (endsNextDay(startTime, endTime)) endDay.setDate(endDay.getDate() + 1);

  const endsAt = atWallClock(endDay, end);
  return {
    id,
    startTime: startsAt,
    endTime: endsAt,
    saleOpensAt:
      leadTimeHours === null ? null : new Date(startsAt.getTime() - leadTimeHours * HOUR_MS),
    saleClosesAt: saleClosesAt(startsAt, endsAt, close),
  };
}

function minutesOfDay(time: { hours: number; minutes: number }): number {
  return time.hours * MINUTES_IN_HOUR + time.minutes;
}

function overlaps(a: SlotHours, b: SlotHours): boolean {
  return a.startTime.getTime() < b.endTime.getTime() && b.startTime.getTime() < a.endTime.getTime();
}

/** Rows landing on an hour the same šablona already sells. The partner throws
 *  them out or moves them; nothing is published while one stands. */
export function collidingRowIds(rows: PlannedOffer[], published: SlotHours[]): string[] {
  return rows.filter((row) => published.some((slot) => overlaps(row, slot))).map((row) => row.id);
}

/** Correcting a row by hand can put two rows on top of each other, which the
 *  pattern itself never does. */
export function overlappingRowIds(rows: PlannedOffer[]): string[] {
  const overlapping = rows.filter((row, index) =>
    rows.some((other, otherIndex) => otherIndex !== index && overlaps(row, other)),
  );
  return overlapping.map((row) => row.id);
}

/** Corrected by hand, a row can end the moment it starts — the pattern itself
 *  is refused for that before any row exists. */
export function rowIdsEndingWhenTheyStart(rows: PlannedOffer[]): string[] {
  return rows
    .filter((row) => row.endTime.getTime() <= row.startTime.getTime())
    .map((row) => row.id);
}

/** The šablona's lead time and sale-close offset are derived independently, so
 *  a close shorter than the lead time lands before the opening. A row missing
 *  either end has nothing to contradict. */
export function rowIdsClosingSaleBeforeItOpens(rows: PlannedOffer[]): string[] {
  return rows
    .filter(
      (row) =>
        row.saleOpensAt !== null &&
        row.saleClosesAt !== null &&
        row.saleClosesAt.getTime() <= row.saleOpensAt.getTime(),
    )
    .map((row) => row.id);
}

/** Selling an ended termín makes no sense: a start-anchored offset longer than
 *  the termín, or a row corrected to hours shorter than the template length. */
export function rowIdsClosingSaleAfterItEnds(rows: PlannedOffer[]): string[] {
  return rows
    .filter((row) => row.saleClosesAt !== null && row.saleClosesAt.getTime() > row.endTime.getTime())
    .map((row) => row.id);
}

/** A derived close in the past ends the sale the moment the termín is
 *  published, which no partner wants from a pattern. */
export function rowIdsWithSaleAlreadyClosed(rows: PlannedOffer[], now: Date): string[] {
  return rows
    .filter((row) => row.saleClosesAt !== null && row.saleClosesAt.getTime() <= now.getTime())
    .map((row) => row.id);
}

/** What the pattern alone gets wrong, said before any preview is drawn. */
export function patternRefusal(pattern: SeriesPattern): string | null {
  if (pattern.weekdays.length === 0) return seriesMessage.noWeekday;
  if (!hasHours(pattern)) return seriesMessage.noHours;
  if (endsWhenItStarts(pattern.startTime, pattern.endTime)) return seriesMessage.endEqualsStart;
  if (startOfLocalDay(pattern.to).getTime() < startOfLocalDay(pattern.from).getTime()) {
    return seriesMessage.emptyRange;
  }
  if (expandSeriesPattern(pattern, noSaleWindowDefaults).length === 0) return seriesMessage.generatesNothing;
  return null;
}

export interface TemplateDefaults {
  priceDiscounted: number | null;
  capacity: number | null;
}

/** What the šablona itself makes impossible, said the moment it is picked —
 *  filling in a pattern that can never be published is wasted work. */
export function templateRefusal(defaults: TemplateDefaults): string | null {
  if (defaults.priceDiscounted === null) return seriesMessage.templateWithoutPrice;
  if (defaults.capacity === null) return seriesMessage.templateWithoutCapacity;
  return null;
}

export interface SubmissionCheck {
  rows: PlannedOffer[];
  published: SlotHours[];
  now?: Date;
}

/** Why these rows cannot be published as they stand. Null = they can. */
export function submissionRefusal({
  rows,
  published,
  now = new Date(),
}: SubmissionCheck): string | null {
  if (rows.length === 0) return seriesMessage.nothingLeft;
  if (rowIdsEndingWhenTheyStart(rows).length > 0) return seriesMessage.rowsEndWhenTheyStart;
  if (rows.some((row) => row.startTime.getTime() <= now.getTime())) {
    return seriesMessage.startsInPast;
  }
  if (rowIdsClosingSaleBeforeItOpens(rows).length > 0) {
    return seriesMessage.saleClosesBeforeItOpens;
  }
  if (rowIdsClosingSaleAfterItEnds(rows).length > 0) return seriesMessage.saleClosesAfterItEnds;
  if (rowIdsWithSaleAlreadyClosed(rows, now).length > 0) return seriesMessage.saleAlreadyClosed;
  if (collidingRowIds(rows, published).length > 0) return seriesMessage.collidesWithPublished;
  if (overlappingRowIds(rows).length > 0) return seriesMessage.collidesWithinSeries;
  return null;
}
